// Browse the team's public channels and join one (ios/Sources/Views/BrowseChannelsSheet.swift,
// shared/API.md §17). The listing is one or more pages of a possibly large team, so typing asks
// the SERVER (debounced) — the local filter of the listing only covers the gap until it answers.
// Channels the reader is already in stay listed, marked, and open instead of joining (§17); they
// sort after the ones that can still be joined, which is what the sheet is for.
import { useEffect, useMemo, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import type { BrowseChannel, BrowseResult } from '../../../data/features/people';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Button, Dialog, EmptyState, InlineStatus, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { useStableClose } from './dialogFocus';
import './people.css';

const DEBOUNCE_MS = 300;

function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function ordered(list: BrowseChannel[]): BrowseChannel[] {
  return [...list].sort((a, b) => (a.joined === b.joined ? 0 : a.joined ? 1 : -1));
}

export function BrowseChannelsDialog({ onClose: onCloseProp }: { onClose(): void }) {
  const onClose = useStableClose(onCloseProp);
  const shell = useShell();
  const teamId = shell.team.id;
  const [listing, setListing] = useState<BrowseResult | null>(null);
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState<{ term: string; channels: BrowseChannel[] } | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  const [reloadTick, setReloadTick] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setListing(null);
    void data.call('browseChannels', teamId).then(
      (r) => live && setListing(r),
      () => live && setListing({ error: 'network' }),
    );
    return () => {
      live = false;
    };
  }, [teamId, reloadTick]);

  const term = query.trim();
  useEffect(() => {
    if (!term) return;
    let live = true;
    const timer = setTimeout(() => {
      void data.call('searchBrowseChannels', teamId, term).then((r) => {
        // A failed search keeps the local filter: never an error for a keystroke.
        if (live && !('error' in r)) setSearched({ term, channels: r.channels });
      });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [teamId, term]);

  const all = listing && !('error' in listing) ? listing.channels : null;
  const visible = useMemo(() => {
    if (!term) return ordered(all ?? []);
    if (searched && searched.term === term) return ordered(searched.channels);
    const needle = fold(term);
    return ordered((all ?? []).filter((c) => fold(c.displayName).includes(needle) || c.name.includes(needle)));
  }, [all, term, searched]);

  useEffect(() => setHighlight(0), [term]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${highlight}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const act = async (channel: BrowseChannel) => {
    if (joiningId) return;
    if (channel.joined) {
      onClose();
      shell.openChannel(channel.id);
      return;
    }
    setJoiningId(channel.id);
    setError(null);
    try {
      const result = await data.call('joinChannel', channel.id);
      if ('channelId' in result) {
        onClose();
        shell.openChannel(result.channelId);
        return;
      }
      setError(t('web.people.browse.joinFailed'));
    } catch {
      setError(t('web.people.browse.joinFailed'));
    }
    setJoiningId(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!visible.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (h + step + visible.length) % visible.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const channel = visible[Math.min(highlight, visible.length - 1)];
      if (channel) void act(channel);
    }
  };

  const footer = (
    <>
      <span className={`people-footer-hint${error ? ' error' : ''}`} role={error ? 'alert' : undefined}>
        {error ?? ''}
      </span>
      <Button kind="secondary" icon="plus" onClick={() => shell.openDialog({ kind: 'createChannel' })}>
        {t('createChannel.title')}
      </Button>
    </>
  );

  return (
    <Dialog title={t('browse.title')} onClose={onClose} footer={footer} width={560}>
      <div className="people-dialog-root">
        <div className="people-sticky" onKeyDown={onKeyDown}>
          <SearchInput value={query} onChange={setQuery} placeholder={t('web.people.browse.search')} autoFocus />
        </div>
        {listing === null ? <InlineStatus kind="loading" text={t('web.people.searching')} /> : null}
        {listing && 'error' in listing ? <InlineStatus kind="error" text={t('browse.failed')} onRetry={() => setReloadTick((n) => n + 1)} /> : null}
        {all && !visible.length ? <EmptyState title={term ? tf('web.people.browse.noMatches', term) : t('browse.empty')} /> : null}
        <div className="people-list" ref={listRef} role="listbox" aria-label={t('browse.title')}>
          {visible.map((channel, index) => {
            const count = channel.memberCount == null ? null : channel.memberCount === 1 ? t('web.people.browse.membersOne') : tf('web.people.browse.members', channel.memberCount);
            const meta = [count, channel.joined ? t('web.people.browse.joined') : null].filter(Boolean).join(' · ');
            return (
              <div
                key={channel.id}
                data-index={index}
                role="option"
                aria-selected={index === highlight}
                className={`list-row people-row${index === highlight ? ' highlighted' : ''}`}
                onMouseMove={() => index !== highlight && setHighlight(index)}
              >
                <span className="list-row-leading">
                  <span className="people-glyph">
                    <Icon name="hash" size={16} />
                  </span>
                </span>
                <span className="list-row-text">
                  <span className="list-row-title">{channel.displayName}</span>
                  {channel.purpose ? <span className="list-row-subtitle">{channel.purpose}</span> : null}
                  {meta ? <span className="list-row-subtitle">{meta}</span> : null}
                </span>
                <span className="list-row-trailing">
                  <button
                    className={`people-trailing-action${channel.joined ? ' quiet' : ''}`}
                    onClick={() => void act(channel)}
                    disabled={joiningId !== null}
                    tabIndex={-1}
                  >
                    {joiningId === channel.id ? '…' : channel.joined ? t('people.open') : t('browse.join')}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </Dialog>
  );
}
